import userDetail from "Stores/userDetail";
import { linkedInReplyState } from "../state";
import PaywallUtils from "Utils/PaywallUtils";
import { makeApi, URLS } from "Utils/apiURL";

const linkedInUtils = {};

const POPUP_POLL_INTERVAL_MS = 1000;
const POPUP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// The account is written to our DB by Unipile's /webhook/notify call, which
// is a separate, async server-to-server request - it can easily still be in
// flight (or not yet arrived) by the moment the popup closes. Poll the
// LiAuthRequest's own status (TO_BE_CONNECTED/CONNECTED/FAILED/DUPLICATE)
// instead of guessing from the account list, and stop as soon as it reaches
// a terminal state instead of always running the full window.
const STATUS_POLL_INTERVAL_MS = 5000;
const STATUS_POLL_MAX_ATTEMPTS = 10;
const TERMINAL_STATUSES = ["CONNECTED", "FAILED", "DUPLICATE"];

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const pollOauthConnectStatus = async () => {
  linkedInReplyState.setOauthConnectStatus("TO_BE_CONNECTED");
  for (let attempt = 0; attempt < STATUS_POLL_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await wait(STATUS_POLL_INTERVAL_MS);
    }
    const status = await linkedInReplyState.getOauthConnectStatus();
    if (TERMINAL_STATUSES.includes(status)) {
      linkedInReplyState.setOauthConnectStatus(status);
      if (status === "CONNECTED") {
        linkedInReplyState.getLinkedInAccounts();
      }
      return;
    }
  }
  // Exhausted all attempts, still TO_BE_CONNECTED - leave the strip as is;
  // only the four LiAuthRequestStatus values are handled here.
};

// Unipile redirects the popup back to our own app (liConnectionPageUrl) on
// both success and cancel. LinkedInAccounts/main.js detects it's running
// inside that popup (window.opener set) and posts LINKEDIN_OAUTH_POPUP_DONE
// before closing itself - postMessage works regardless of origin, unlike
// reading popup.location, which is blocked cross-origin (e.g. when the
// popup's return URL doesn't match the opener's own origin, as in local dev
// against a staging backend). The closed-poll below is only a fallback for
// the plain "user closed the popup manually" case.
const openOAuthPopupAndAwaitClose = popup => {
  return new Promise(resolve => {
    if (!popup) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearInterval(intervalId);
      window.removeEventListener("message", onMessage);
      if (!popup.closed) {
        popup.close();
      }
      resolve();
    };

    const onMessage = event => {
      if (event?.data?.type === "LINKEDIN_OAUTH_POPUP_DONE") {
        finish();
      }
    };
    window.addEventListener("message", onMessage);

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      const timedOut = Date.now() - startedAt > POPUP_POLL_TIMEOUT_MS;
      if (popup.closed || timedOut) {
        finish();
      }
    }, POPUP_POLL_INTERVAL_MS);
  });
};

// Connects (or reconnects, when accountId is provided) a LinkedIn account via
// the Unipile-hosted OAuth flow. Account type (standard vs. Sales Navigator)
// is detected by the backend during connect - no user choice needed here.
linkedInUtils.connectViaOAuth = async (accountId = null) => {
  const {
    planName = "",
    inFreePlan = false,
    liAutomationEnabled = false,
    noOfLiAccountsUsed = 0,
    noOfLiAccountsEligible = 0
  } = userDetail?.userFeatureFlag || {};

  await linkedInReplyState.getCurrentTeam();
  if (!userDetail?.userFeatureFlag?.liAutomationEnabled) {
    PaywallUtils.upgradeUtilsFunction("linkedInAutomationNotEnabled", false);
    return;
  }
  if (!liAutomationEnabled) {
    return;
  }
  if (!accountId && noOfLiAccountsUsed >= noOfLiAccountsEligible) {
    PaywallUtils.upgradeUtilsFunction(
      "multipleLinkedInCustom",
      false,
      planName,
      inFreePlan
    );
    return;
  }

  try {
    const config = accountId
      ? {
          url: `${URLS.getLinkedInOAuthReconnectUrl}?accountId=${accountId}`,
          method: "GET"
        }
      : { url: URLS.getLinkedInOAuthConnectUrl, method: "GET" };
    const res = await makeApi(config);
    const authUrl = res?.data?.authUrl || res?.authUrl;
    if (!authUrl) {
      linkedInReplyState.setLinkedInMsg(
        "Unable to start the LinkedIn connection. Please try again."
      );
      linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
      return;
    }
    const popup = window.open(
      authUrl,
      "linkedInOAuthConnect",
      "width=500,height=700"
    );
    await openOAuthPopupAndAwaitClose(popup);
    pollOauthConnectStatus();
  } catch (e) {
    linkedInReplyState.setLinkedInMsg(
      "Something went wrong while connecting your LinkedIn account."
    );
    linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
  }
};

// Recent-terminal-result window: a CONNECTED/FAILED/DUPLICATE result older
// than this is treated as history, not something to surface again - without
// this, every visit to the page would re-show whatever happened last time,
// even from weeks ago.
const RECENT_TERMINAL_RESULT_WINDOW_MS = 2 * 60 * 1000;

// Called once when the LinkedIn settings page loads. Covers the case where
// a connect attempt was in flight (or just resolved) when the tab was
// closed, the page was refreshed, or the user logged out and back in -
// none of which the in-memory poll loop above survives. Reads the same
// DB-backed status endpoint the poll loop uses, so there's no separate
// source of truth to keep in sync.
linkedInUtils.resumeOauthConnectStatusIfNeeded = async () => {
  const req = await linkedInReplyState.getOauthConnectRequest();
  const status = req?.status;
  if (!status) {
    return;
  }

  if (status === "TO_BE_CONNECTED") {
    const notExpired = !req.expiry || new Date(req.expiry).getTime() > Date.now();
    if (notExpired) {
      pollOauthConnectStatus();
    }
    return;
  }

  if (TERMINAL_STATUSES.includes(status)) {
    const resolvedAt = req.updateTs ? new Date(req.updateTs).getTime() : 0;
    const isRecent = Date.now() - resolvedAt < RECENT_TERMINAL_RESULT_WINDOW_MS;
    if (isRecent) {
      linkedInReplyState.setOauthConnectStatus(status);
      if (status === "CONNECTED") {
        linkedInReplyState.getLinkedInAccounts();
      }
    }
  }
};

linkedInUtils.handleSalesNavigatorButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

linkedInUtils.handleButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

export { linkedInUtils };
export default linkedInUtils;
